import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  HiveWorkflow,
  WfNode,
  WfEdge,
  WfIssue,
  WfTemplate,
  WfControls,
} from "@/types";
import {
  nodeDef,
  defaultPlacement,
  buildGraph,
} from "@/data/mockWorkflowNodes";
import { useHive } from "@/store/useHive";
import { useExecution } from "@/store/executionStore";
import { api, ApiError } from "@/api/client";
import { gatedProductsIn } from "@/lib/entitlements";
import type { EntitlementMap, Paged, ProductId } from "@/api/types";
import { uid } from "@/lib/utils";

export type DrawerTab = "validation" | "test" | "logs" | "approvals" | "cost";

/* ── Derived helpers (pure) ───────────────────────────────────────────────── */

export interface PermissionFootprint {
  reads: string[];
  writes: string[];
  external: boolean;
  code: boolean;
  pr: boolean;
  deploy: boolean;
  budget: boolean;
}

export function permissionFootprint(wf: HiveWorkflow): PermissionFootprint {
  const reads = new Set<string>();
  const writes = new Set<string>();
  let external = false,
    code = false,
    pr = false,
    deploy = false,
    budget = false;
  wf.nodes.forEach((n) => {
    if (n.disabled) return;
    const d = nodeDef(n.defKey);
    d.reads?.forEach((r) => reads.add(r));
    d.writes?.forEach((w) => writes.add(w));
    external = external || !!d.external;
    code = code || !!d.code;
    pr = pr || !!d.pr;
    deploy = deploy || !!d.deploy;
    budget = budget || !!d.budget;
  });
  return { reads: [...reads], writes: [...writes], external, code, pr, deploy, budget };
}

/** Non-enabled products among a workflow's active steps (mirrors the server's
 *  activation validation — used to swap Publish for the Early-Access CTA). */
export function workflowGatedProducts(
  wf: Pick<HiveWorkflow, "nodes">,
  entitlements: EntitlementMap | null,
): ProductId[] {
  return gatedProductsIn(
    wf.nodes.map((n) => ({ integration: nodeDef(n.defKey).integration, disabled: n.disabled })),
    entitlements,
  );
}

export function highestRisk(wf: HiveWorkflow): WfNode | null {
  const order = { low: 0, medium: 1, high: 2, restricted: 3 };
  let top: WfNode | null = null;
  let topR = -1;
  wf.nodes.forEach((n) => {
    if (n.disabled) return;
    const r = order[nodeDef(n.defKey).risk];
    if (r > topR) {
      topR = r;
      top = n;
    }
  });
  return top;
}

/** Static validation of a workflow → ordered findings for the bottom drawer. */
export function validateWorkflow(wf: HiveWorkflow): WfIssue[] {
  const issues: WfIssue[] = [];
  const active = wf.nodes.filter((n) => !n.disabled);
  const hooks = active.filter((n) => nodeDef(n.defKey).kind === "hook");
  const pubs = active.filter((n) => nodeDef(n.defKey).kind === "publish");
  const hasGate = active.some((n) => nodeDef(n.defKey).gate || n.approval);

  if (hooks.length === 0)
    issues.push({ level: "error", text: "Workflow has no Hook — nothing will start it." });
  if (hooks.length > 1)
    issues.push({ level: "warning", text: `${hooks.length} hooks defined — only the first will trigger in sandbox.` });
  if (pubs.length === 0)
    issues.push({ level: "warning", text: "No Publisher — this workflow produces no outward result." });

  // Required configuration
  active.forEach((n) => {
    if (n.configComplete < 0.6)
      issues.push({ level: "error", text: `“${n.title}” is missing required configuration.`, nodeId: n.id });
  });

  // Connectivity
  const connected = new Set<string>();
  wf.edges.forEach((e) => {
    connected.add(e.source);
    connected.add(e.target);
  });
  if (active.length > 1)
    active.forEach((n) => {
      if (!connected.has(n.id))
        issues.push({ level: "error", text: `“${n.title}” is disconnected from the flow.`, nodeId: n.id });
    });

  // Risk / approval policy
  const restricted = active.filter((n) => nodeDef(n.defKey).risk === "restricted");
  if (restricted.length && !hasGate)
    issues.push({
      level: "error",
      text: `Restricted action “${restricted[0].title}” requires a Human Approval Gate before publishing.`,
      nodeId: restricted[0].id,
    });

  const fp = permissionFootprint(wf);
  if (fp.deploy && wf.env === "production" && !hasGate)
    issues.push({ level: "warning", text: "Production deploy without an approval gate — admin approval will be enforced." });
  if (fp.external && wf.env === "production" && !hasGate)
    issues.push({ level: "warning", text: "Posts externally without approval — verify the audience before publishing." });

  // Suggestions
  if (!active.some((n) => n.defKey === "proc.summarize"))
    issues.push({ level: "suggestion", text: "Add “Summarize Execution” for cleaner handoffs and audit trails." });
  if (fp.budget && !active.some((n) => n.defKey === "proc.cost"))
    issues.push({ level: "suggestion", text: "Add a “Cost Estimate” step — this workflow spends model budget." });

  return issues;
}

const blankControls: WfControls = {
  maxRunsPerHour: 30,
  maxCostPerRun: 10,
  approvalCostThreshold: 8,
  pauseOnRepeatedFailures: true,
  disableExternalInSandbox: true,
  blockProdDeployWithoutAdmin: true,
};

/* ── Store ────────────────────────────────────────────────────────────────── */

interface WorkflowState {
  workflows: HiveWorkflow[];
  selectedWorkflowId: string;
  selectedNodeId: string | null;

  hydrate: () => Promise<void>;

  drawerOpen: boolean;
  drawerTab: DrawerTab;
  search: string;
  libraryFilter: string;
  templatesOpen: boolean;
  publishWorkflowId: string | null;
  /** A test run was requested (top bar / palette) and the panel hasn't started it yet. */
  testPending: boolean;
  ackTest: () => void;

  selectWorkflow: (id: string) => void;
  selectNode: (id: string | null) => void;
  setDrawer: (open: boolean) => void;
  setDrawerTab: (t: DrawerTab) => void;
  setSearch: (s: string) => void;
  setLibraryFilter: (f: string) => void;
  setTemplatesOpen: (v: boolean) => void;

  newWorkflow: () => void;
  newWorkflowFromHook: (defKey: string) => void;
  applyTemplate: (tpl: WfTemplate) => void;
  duplicateWorkflow: (id: string) => void;
  renameWorkflow: (id: string, name: string) => void;
  setDescription: (id: string, d: string) => void;
  setEnv: (id: string, env: HiveWorkflow["env"]) => void;
  updateControls: (id: string, patch: Partial<WfControls>) => void;

  addNode: (defKey: string) => void;
  deleteNode: (nodeId: string) => void;
  duplicateNode: (nodeId: string) => void;
  moveNode: (nodeId: string, x: number, y: number) => void;
  renameNode: (nodeId: string, title: string) => void;
  toggleNodeDisabled: (nodeId: string) => void;
  toggleNodeApproval: (nodeId: string) => void;
  setNodeConfig: (nodeId: string, key: string, value: string | number | boolean) => void;

  connect: (source: string, target: string) => void;
  deleteEdge: (edgeId: string) => void;

  requestPublish: (id: string) => void;
  cancelPublish: () => void;
  confirmPublish: (id: string) => void;
  pauseWorkflow: (id: string) => void;
  rollback: (id: string, version: string) => void;

  runTest: (id: string) => void;
  fireTest: () => void;
  saveDraft: (id: string) => void;
}

function patch(
  wfs: HiveWorkflow[],
  id: string,
  fn: (wf: HiveWorkflow) => HiveWorkflow
): HiveWorkflow[] {
  return wfs.map((w) => (w.id === id ? fn(w) : w));
}

export const useWorkflow = create<WorkflowState>()(
  persist(
    (set, get) => {
      // Debounced write-behind: editor mutations apply locally, then the full
      // workflow document is PATCHed to the server once the burst settles.
      const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();
      const persistWorkflow = (id: string, delayMs = 800) => {
        if (!id) return;
        clearTimeout(persistTimers.get(id));
        persistTimers.set(
          id,
          setTimeout(() => {
            persistTimers.delete(id);
            const wf = get().workflows.find((w) => w.id === id);
            if (!wf) return;
            api.patch<HiveWorkflow>(`/api/v1/workflows/${id}`, wf).catch(() =>
              useHive.getState().pushToast({ title: "Sync failed", body: "Workflow change wasn't saved — retry", tone: "error" })
            );
          }, delayMs)
        );
      };
      // Create on the server, then adopt the server-assigned entity locally.
      const createWorkflow = (draft: Omit<HiveWorkflow, "id">, toast: { title: string; body: string }) => {
        api
          .post<HiveWorkflow>("/api/v1/workflows", draft)
          .then((wf) => {
            set((s) => ({
              workflows: [wf, ...s.workflows],
              selectedWorkflowId: wf.id,
              selectedNodeId: null,
              templatesOpen: false,
            }));
            useHive.getState().pushToast({ ...toast, tone: "success" });
          })
          .catch(() =>
            useHive.getState().pushToast({ title: "Create failed", body: "Workflow couldn't be created — retry", tone: "error" })
          );
      };

      return {
      workflows: [],
      selectedWorkflowId: "",
      selectedNodeId: null,

      hydrate: async () => {
        try {
          const res = await api.get<Paged<HiveWorkflow>>("/api/v1/workflows?limit=100&sort=created_at&order=asc");
          set((s) => ({
            workflows: res.items,
            selectedWorkflowId: res.items.some((w) => w.id === s.selectedWorkflowId)
              ? s.selectedWorkflowId
              : res.items[0]?.id ?? "",
          }));
        } catch {
          useHive.getState().pushToast({ title: "Load failed", body: "Couldn't load workflows — retry", tone: "error" });
        }
      },

      drawerOpen: true,
      drawerTab: "validation",
      search: "",
      libraryFilter: "all",
      templatesOpen: false,
      publishWorkflowId: null,
      testPending: false,

      selectWorkflow: (id) => set({ selectedWorkflowId: id, selectedNodeId: null }),
      selectNode: (id) => set({ selectedNodeId: id }),
      setDrawer: (open) => set({ drawerOpen: open }),
      setDrawerTab: (t) => set({ drawerTab: t, drawerOpen: true }),
      setSearch: (s) => set({ search: s }),
      setLibraryFilter: (f) => set({ libraryFilter: f }),
      setTemplatesOpen: (v) => set({ templatesOpen: v }),

      newWorkflow: () => {
        const { nodes, edges } = buildGraph(["hook.manual"], [], []);
        const wf: Omit<HiveWorkflow, "id"> = {
          name: "Untitled Workflow",
          description: "Describe what this workflow automates…",
          status: "draft",
          env: "sandbox",
          owner: "You",
          version: "v0.1",
          risk: "low",
          triggerHook: "Manual Run",
          lastRun: "—",
          successRate: 0,
          avgDuration: "—",
          runsThisWeek: 0,
          failedRuns: 0,
          integrations: [],
          nodes,
          edges,
          history: [{ version: "v0.1", at: "now", by: "You", note: "Created draft" }],
          controls: { ...blankControls },
          cost: { model: 0, cloud: 0, verification: 0, byoncSavings: 0, estRuntime: "—" },
        };
        createWorkflow(wf, { title: "Draft created", body: "New workflow ready to build" });
      },

      newWorkflowFromHook: (defKey) => {
        const def = nodeDef(defKey);
        const { nodes, edges } = buildGraph([defKey], [], []);
        const wf: Omit<HiveWorkflow, "id"> = {
          name: `${def.label} workflow`,
          description: `Starts when: ${def.description} Add processes and a publisher to complete the chain.`,
          status: "draft",
          env: "sandbox",
          owner: "You",
          version: "v0.1",
          risk: "low",
          triggerHook: def.label,
          lastRun: "—",
          successRate: 0,
          avgDuration: "—",
          runsThisWeek: 0,
          failedRuns: 0,
          integrations: def.integration && def.integration !== "hive" ? [def.integration] : [],
          nodes,
          edges,
          history: [{ version: "v0.1", at: "now", by: "You", note: `Created from hook · ${def.label}` }],
          controls: { ...blankControls },
          cost: { model: 0, cloud: 0, verification: 0, byoncSavings: 0, estRuntime: "—" },
        };
        createWorkflow(wf, { title: "Hook wired up", body: `${def.label} draft ready — add processes & publishers` });
      },

      applyTemplate: (tpl) => {
        const { nodes, edges } = buildGraph(tpl.hookKeys, tpl.processKeys, tpl.publishKeys);
        const integrations = new Set<string>();
        [...tpl.hookKeys, ...tpl.processKeys, ...tpl.publishKeys].forEach((k) => {
          const i = nodeDef(k).integration;
          if (i && i !== "hive") integrations.add(i);
        });
        const wf: Omit<HiveWorkflow, "id"> = {
          name: tpl.name,
          description: tpl.description,
          status: "draft",
          env: "sandbox",
          owner: "You",
          version: "v0.1",
          risk: tpl.risk,
          triggerHook: nodeDef(tpl.hookKeys[0]).label,
          lastRun: "—",
          successRate: 0,
          avgDuration: "—",
          runsThisWeek: 0,
          failedRuns: 0,
          integrations: [...integrations],
          nodes,
          edges,
          history: [{ version: "v0.1", at: "now", by: "You", note: `From template · ${tpl.name}` }],
          controls: { ...blankControls },
          cost: { model: 0, cloud: 0, verification: 0, byoncSavings: 0, estRuntime: "—" },
        };
        createWorkflow(wf, { title: "Template added", body: `${tpl.name} ready in sandbox` });
      },

      duplicateWorkflow: (id) => {
        const src = get().workflows.find((w) => w.id === id);
        if (!src) return;
        const idMap = new Map<string, string>();
        const nodes = src.nodes.map((n) => {
          const nid = uid("n");
          idMap.set(n.id, nid);
          return { ...n, id: nid };
        });
        const edges = src.edges.map((e) => ({
          ...e,
          id: uid("e"),
          source: idMap.get(e.source)!,
          target: idMap.get(e.target)!,
        }));
        const { id: _srcId, ...rest } = src;
        const copy: Omit<HiveWorkflow, "id"> = {
          ...rest,
          name: `${src.name} (copy)`,
          status: "draft",
          version: "v0.1",
          nodes,
          edges,
          history: [{ version: "v0.1", at: "now", by: "You", note: `Duplicated from ${src.name}` }],
        };
        createWorkflow(copy, { title: "Workflow duplicated", body: copy.name });
      },

      renameWorkflow: (id, name) => {
        set((s) => ({ workflows: patch(s.workflows, id, (w) => ({ ...w, name })) }));
        persistWorkflow(id);
      },
      setDescription: (id, description) => {
        set((s) => ({ workflows: patch(s.workflows, id, (w) => ({ ...w, description })) }));
        persistWorkflow(id);
      },
      setEnv: (id, env) => {
        set((s) => ({ workflows: patch(s.workflows, id, (w) => ({ ...w, env })) }));
        persistWorkflow(id);
      },
      updateControls: (id, p) => {
        set((s) => ({ workflows: patch(s.workflows, id, (w) => ({ ...w, controls: { ...w.controls, ...p } })) }));
        persistWorkflow(id);
      },

      addNode: (defKey) => {
        const id = get().selectedWorkflowId;
        const def = nodeDef(defKey);
        let newId = "";
        set((s) => ({
          workflows: patch(s.workflows, id, (w) => {
            const count = w.nodes.filter((n) => nodeDef(n.defKey).kind === def.kind).length;
            const { x, y } = defaultPlacement(def.kind, count);
            newId = uid("n");
            const node: WfNode = {
              id: newId,
              defKey,
              x,
              y,
              title: def.label,
              approval: def.gate,
              config: {},
              configComplete: def.kind === "hook" || Object.keys(node_defaults(def)).length === 0 ? 1 : 0.5,
            };
            return { ...w, nodes: [...w.nodes, node] };
          }),
        }));
        set({ selectedNodeId: newId });
        persistWorkflow(id);
        useHive.getState().pushToast({ title: "Node added", body: def.label, tone: "info" });
      },

      deleteNode: (nodeId) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            nodes: w.nodes.filter((n) => n.id !== nodeId),
            edges: w.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
          })),
          selectedNodeId: s.selectedNodeId === nodeId ? null : s.selectedNodeId,
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      duplicateNode: (nodeId) => {
        set((s) => {
          let nid = "";
          const wfs = patch(s.workflows, s.selectedWorkflowId, (w) => {
            const src = w.nodes.find((n) => n.id === nodeId);
            if (!src) return w;
            nid = uid("n");
            const node: WfNode = { ...src, id: nid, x: src.x + 40, y: src.y + 40, title: `${src.title} copy` };
            return { ...w, nodes: [...w.nodes, node] };
          });
          return { workflows: wfs, selectedNodeId: nid || s.selectedNodeId };
        });
        persistWorkflow(get().selectedWorkflowId);
      },

      moveNode: (nodeId, x, y) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            nodes: w.nodes.map((n) => (n.id === nodeId ? { ...n, x, y } : n)),
          })),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      renameNode: (nodeId, title) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            nodes: w.nodes.map((n) => (n.id === nodeId ? { ...n, title } : n)),
          })),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      toggleNodeDisabled: (nodeId) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            nodes: w.nodes.map((n) => (n.id === nodeId ? { ...n, disabled: !n.disabled } : n)),
          })),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      toggleNodeApproval: (nodeId) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            nodes: w.nodes.map((n) => (n.id === nodeId ? { ...n, approval: !n.approval } : n)),
          })),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      setNodeConfig: (nodeId, key, value) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            nodes: w.nodes.map((n) =>
              n.id === nodeId
                ? { ...n, config: { ...n.config, [key]: value }, configComplete: 1 }
                : n
            ),
          })),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      connect: (source, target) => {
        if (source === target) return;
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => {
            if (w.edges.some((e) => e.source === source && e.target === target)) return w;
            // prevent trivial 2-cycles
            if (w.edges.some((e) => e.source === target && e.target === source)) return w;
            const edge: WfEdge = { id: uid("e"), source, target, tone: "default" };
            return { ...w, edges: [...w.edges, edge] };
          }),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      deleteEdge: (edgeId) => {
        set((s) => ({
          workflows: patch(s.workflows, s.selectedWorkflowId, (w) => ({
            ...w,
            edges: w.edges.filter((e) => e.id !== edgeId),
          })),
        }));
        persistWorkflow(get().selectedWorkflowId);
      },

      requestPublish: (id) => set({ publishWorkflowId: id }),
      cancelPublish: () => set({ publishWorkflowId: null }),
      // Activation goes through the server's publish route so the entitlement
      // validation there is authoritative (a workflow containing steps from a
      // non-enabled product is rejected with `workflow_product_not_enabled`).
      confirmPublish: (id) => {
        set({ publishWorkflowId: null });
        api
          .post<HiveWorkflow>(`/api/v1/workflows/${id}/publish`)
          .then((updated) => {
            set((s) => ({
              workflows: patch(s.workflows, id, (w) => ({
                ...w,
                ...updated,
                version: bump(w.version),
                history: [
                  { version: bump(w.version), at: "now", by: "You", note: "Published to " + updated.env },
                  ...w.history,
                ],
              })),
            }));
            persistWorkflow(id, 0);
            const wf = get().workflows.find((w) => w.id === id);
            useHive.getState().pushToast({ title: "Workflow published", body: `${wf?.name} is now active`, tone: "success" });
            useHive.getState().pushNotification({ title: "Workflow published", body: `${wf?.name} is live in ${wf?.env}.`, tone: "success", app: "workflows" });
            useHive.getState().pushActivity({ kind: "job", title: "Workflow published", detail: wf?.name ?? "", app: "workflows" });
          })
          .catch((err) => activationError(err, "Publish blocked"));
      },

      pauseWorkflow: (id) => {
        const wf = get().workflows.find((w) => w.id === id);
        const action = wf?.status === "paused" ? "resume" : "pause";
        api
          .post<HiveWorkflow>(`/api/v1/workflows/${id}/${action}`)
          .then((updated) => {
            set((s) => ({ workflows: patch(s.workflows, id, (w) => ({ ...w, ...updated })) }));
            useHive.getState().pushToast({
              title: action === "pause" ? "Workflow paused" : "Workflow resumed",
              body: wf?.name ?? "",
              tone: action === "pause" ? "warning" : "success",
            });
          })
          .catch((err) => activationError(err, "Resume blocked"));
      },

      rollback: (id, version) => {
        set((s) => {
          useHive.getState().pushToast({ title: "Rolled back", body: `Restored ${version}`, tone: "info" });
          return { workflows: patch(s.workflows, id, (w) => ({ ...w, version })) };
        });
        persistWorkflow(id, 0);
      },

      runTest: (id) => {
        const wf = get().workflows.find((w) => w.id === id);
        if (!wf) return;
        const exec = useExecution.getState().createFromWorkflow(wf, true);
        useHive.getState().pushToast({ title: "Sandbox test started", body: `${wf.name} · simulated run`, tone: "info" });
        useHive.getState().pushNotification({ title: "Sandbox test started", body: `${wf.name} is running in sandbox.`, tone: "info", app: "execution" });
        useHive.getState().pushActivity({ kind: "job", title: "Sandbox test", detail: wf.name, app: "workflows" });
        return exec;
      },

      fireTest: () => set({ testPending: true, drawerTab: "test", drawerOpen: true }),
      ackTest: () => set({ testPending: false }),

      saveDraft: (id) => {
        const wf = get().workflows.find((w) => w.id === id);
        persistWorkflow(id, 0);
        useHive.getState().pushToast({ title: "Draft saved", body: `${wf?.name} · ${wf?.version}`, tone: "success" });
      },
      };
    },
    {
      name: "hive-workflow-ui",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        selectedWorkflowId: s.selectedWorkflowId,
        drawerTab: s.drawerTab,
        drawerOpen: s.drawerOpen,
        libraryFilter: s.libraryFilter,
      }),
    }
  )
);

/** Surface an activation refusal (entitlement validation) as a clear toast. */
function activationError(err: unknown, title: string): void {
  const detail =
    err instanceof ApiError && err.code === "workflow_product_not_enabled"
      ? err.message
      : "Change wasn't saved — retry";
  useHive.getState().pushToast({ title, body: detail, tone: "error" });
}

function node_defaults(def: { integration?: string; ai?: boolean; gate?: boolean }) {
  // mirrors the catalog's default config requirement — used only to decide
  // whether a freshly-added node still needs configuration.
  const out: Record<string, boolean> = {};
  if (def.gate) out.approver = true;
  if (def.ai) out.inference = true;
  return out;
}

function bump(v: string) {
  const m = v.match(/v(\d+)\.(\d+)/);
  if (!m) return "v1.0";
  return `v${m[1]}.${Number(m[2]) + 1}`;
}
